import { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    project_type: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry(form);
      setSent(true);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", project_type: "" });
    } catch (e) {
      toast.error("Couldn't send. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <div className="eyebrow mb-5">Get in touch</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight">
          Say hello to <span className="italic">the workshop</span>.
        </h1>
        <p className="mt-6 text-[#2C2421]/75 leading-relaxed text-lg">
          For commissions, trade enquiries, gift cards, or just to talk timber —
          we read every message and reply within 48 hours.
        </p>

        <div className="mt-12 space-y-6 text-[#2C2421]">
          <div className="flex items-start gap-4">
            <Mail size={18} className="mt-1 text-[#6F4E37]" />
            <div>
              <div className="eyebrow !text-[#A89F91] mb-1">Email</div>
              <a href="mailto:thewoodworkersshed@outlook.co.nz" className="link-underline">thewoodworkersshed@outlook.co.nz</a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MapPin size={18} className="mt-1 text-[#6F4E37]" />
            <div>
              <div className="eyebrow !text-[#A89F91] mb-1">Workshop</div>
              <div>Whangamata, Aotearoa</div>
              <div className="text-[#6F4E37] text-sm">Visits by appointment</div>
            </div>
          </div>
        </div>
      </div>

      <div className="md:col-span-7">
        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="bg-[#F5F2EB] p-8 md:p-12 border border-[#E5E0D8]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="eyebrow !text-[#6F4E37]">Name</label>
              <Input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus-visible:ring-0 focus-visible:border-[#2C2421] px-0"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="eyebrow !text-[#6F4E37]">Email</label>
              <Input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus-visible:ring-0 focus-visible:border-[#2C2421] px-0"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="eyebrow !text-[#6F4E37]">Phone (optional)</label>
              <Input
                data-testid="contact-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus-visible:ring-0 focus-visible:border-[#2C2421] px-0"
                placeholder="+64…"
              />
            </div>
            <div>
              <label className="eyebrow !text-[#6F4E37]">Project type</label>
              <Input
                data-testid="contact-project-type"
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                className="mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus-visible:ring-0 focus-visible:border-[#2C2421] px-0"
                placeholder="Commission, gift, trade…"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="eyebrow !text-[#6F4E37]">Subject</label>
            <Input
              data-testid="contact-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus-visible:ring-0 focus-visible:border-[#2C2421] px-0"
              placeholder="What's it about?"
            />
          </div>

          <div className="mt-6">
            <label className="eyebrow !text-[#6F4E37]">Message</label>
            <Textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={6}
              className="mt-2 bg-transparent rounded-none border border-[#A89F91]/40 focus-visible:ring-0 focus-visible:border-[#2C2421] resize-none"
              placeholder="Tell us what you're after."
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="mt-8 inline-flex items-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-7 py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors disabled:opacity-60"
          >
            {submitting ? "Sending…" : (<><Send size={14}/> Send message</>)}
          </button>

          {sent && (
            <div data-testid="contact-success" className="mt-6 text-sm text-[#4A5D4E]">
              Thanks — we'll reply within 48 hours.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
